import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Crown,
  Building2,
  Boxes,
  ArrowLeft,
  Truck,
  Receipt,
  Layers,
  TrendingUp,
} from "lucide-react";
import { track } from "../lib/api";

/**
 * Quick Jump — the "First Scan" tile bar.
 * Each tile = one thing the AI found that demands the user's attention.
 * Tiles hide themselves when their data is unsupported for the current tenant
 * (e.g. branch comparison is hidden when there's only one branch).
 */
function fmtMoneyShort(n) {
  if (n == null) return "—";
  const v = Math.abs(n);
  if (v >= 1_000_000) return `₪${(n / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `₪${Math.round(n / 1_000)}K`;
  return `₪${Math.round(n)}`;
}

export default function QuickJumpBar({ invKpis, summary, zeroPrice, onTheWay, families }) {
  const navigate = useNavigate();

  const go = (to, label) => {
    track("quick_jump", "/", { to, label });
    navigate(to);
  };

  const branchCount = summary?.branches_count || 0;

  const tiles = [
    {
      id: "inventory",
      show: !!invKpis?.available,
      title: "מלאי דחוף",
      sub: invKpis
        ? `${invKpis.critical_restock_count} דחוף · ${invKpis.stockouts_count} חסר · ${fmtMoneyShort(invKpis.tied_up_capital)} תקוע`
        : "טוען...",
      icon: Boxes,
      color: "from-primary/15 to-primary/0",
      ring: "group-hover:ring-primary/40",
      iconColor: "text-primary",
      onClick: () => go("/inventory", "inventory"),
    },
    {
      id: "on-the-way",
      show: onTheWay?.supported,
      title: "סחורה בדרך",
      sub: onTheWay
        ? `${onTheWay.total_rows.toLocaleString()} שורות · ${Math.round(onTheWay.total_units_pending).toLocaleString()} יח'`
        : "—",
      icon: Truck,
      color: "from-sky-400/15 to-sky-500/0",
      ring: "group-hover:ring-sky-400/40",
      iconColor: "text-sky-300",
      onClick: () => go("/on-the-way", "on-the-way"),
    },
    {
      id: "zero-price",
      show: zeroPrice && zeroPrice.total_count >= 0,
      title: "חשבוניות במחיר 0",
      sub: zeroPrice
        ? `${zeroPrice.total_count} שורות · חשד לפספוס תמחור`
        : "—",
      icon: Receipt,
      color: "from-amber-400/15 to-amber-500/0",
      ring: "group-hover:ring-amber-400/40",
      iconColor: "text-amber-300",
      onClick: () => go("/zero-price", "zero-price"),
    },
    {
      id: "families",
      show: families?.supported,
      title: "רווחיות לפי משפחה",
      sub: families
        ? `${families.total_count} משפחות · מובילה: ${(families.top_name || "—").slice(0, 22)}`
        : "Family mapping required",
      icon: Layers,
      color: "from-emerald-400/15 to-emerald-500/0",
      ring: "group-hover:ring-emerald-400/40",
      iconColor: "text-emerald-300",
      onClick: () => go("/families", "families"),
    },
    {
      id: "sales",
      show: true,
      title: "מכירות מובילות",
      sub: summary
        ? `${(summary.products_count || 0).toLocaleString()} מק״טים פעילים`
        : "פריטים שמייצרים את המחזור",
      icon: Crown,
      color: "from-amber-400/20 to-amber-500/0",
      ring: "group-hover:ring-amber-400/40",
      iconColor: "text-amber-300",
      onClick: () => go("/insights#top-sellers", "sales"),
    },
    {
      id: "recommendations",
      show: true,
      title: "המלצות רכש חכמות",
      sub: "מלאי × סחורה בדרך × מכירות",
      icon: TrendingUp,
      color: "from-primary/20 to-primary/0",
      ring: "group-hover:ring-primary/40",
      iconColor: "text-primary",
      onClick: () => go("/smart-restock", "smart-restock"),
    },
    {
      id: "branches",
      // Hide for single-branch tenants — branch comparison is meaningless there
      show: branchCount > 1,
      title: "השוואת סניפים",
      sub: summary
        ? `${branchCount} סניפים · ${(summary.invoices_count || 0).toLocaleString()} חשבוניות`
        : "—",
      icon: Building2,
      color: "from-sky-400/15 to-sky-500/0",
      ring: "group-hover:ring-sky-400/40",
      iconColor: "text-sky-300",
      onClick: () => go("/insights#branches", "branches"),
    },
  ];

  const visible = tiles.filter((t) => t.show);

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 mb-5" data-testid="quick-jump-bar">
      {visible.map((t, i) => (
        <motion.button
          key={t.id}
          onClick={t.onClick}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: i * 0.04 }}
          className={`group relative bg-card border border-border rounded-md px-3 py-3 text-right hover:border-primary/40 transition-all duration-200 overflow-hidden ring-1 ring-transparent ${t.ring}`}
          data-testid={`quick-jump-${t.id}`}
        >
          <div className={`absolute inset-0 bg-gradient-to-bl ${t.color} opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none`} />
          <div className="flex items-center justify-between mb-1 relative">
            <ArrowLeft className="w-3 h-3 text-zinc-600 group-hover:text-primary group-hover:-translate-x-0.5 transition-all" />
            <t.icon className={`w-4 h-4 ${t.iconColor}`} strokeWidth={1.5} />
          </div>
          <div className="text-sm font-medium text-foreground relative leading-tight">{t.title}</div>
          <div className="text-[10px] text-zinc-500 mt-1 relative leading-snug font-mono truncate">{t.sub}</div>
        </motion.button>
      ))}
    </div>
  );
}
